import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useAssignZoneToGroups } from '@/core/hooks/useGroup'
import { useAddContentToZone } from '@/core/hooks/useZone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface UseZoneFinishFlowProps {
	newZoneId: number | null
	refetchZones: () => Promise<unknown>
	onFinishAssignSuccess: () => void
	onFinishAddContentSuccess: () => void
}

export const useZoneFinishFlow = ({
	newZoneId,
	refetchZones,
	onFinishAssignSuccess,
	onFinishAddContentSuccess,
}: UseZoneFinishFlowProps) => {
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const config = useAccountConfig({})

	const { mutate: assignToGroups, isPending: isAssigning } =
		useAssignZoneToGroups({
			config,
			options: {
				onSuccess: () => {
					refetchZones()
					onFinishAssignSuccess()
				},
				onError: () => {
					showNotification({
						type: 'error',
						message: tCommon('entities.group.add_zones.error.title'),
						description: tCommon('entities.group.add_zones.error.description'),
					})
				},
			},
		})

	const { mutate: addContent, isPending: isAddingContent } =
		useAddContentToZone({
			config,
			options: {
				onSuccess: () => {
					refetchZones()
					onFinishAddContentSuccess()
				},
				onError: () => {
					showNotification({
						type: 'error',
						message: t('details.addContentModal.errorTitle'),
						description: t('details.addContentModal.errorDescription'),
					})
				},
			},
		})

	const confirmFinishAssign = (selectedGroupIds: number[]) => {
		if (!newZoneId) return
		assignToGroups({ zoneId: newZoneId, groupIds: selectedGroupIds })
	}

	const confirmFinishAddContent = (
		playlistIds: number[],
		scheduleIds: number[]
	) => {
		if (!newZoneId) return
		addContent({ zoneId: newZoneId, playlistIds, scheduleIds })
	}

	return {
		isSubmitting: isAssigning || isAddingContent,
		confirmFinishAssign,
		confirmFinishAddContent,
	}
}
